import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { matchPath, useLocation } from 'react-router';

import debounce from 'lodash/debounce';

import {
  chainsPagePath,
  modelApisPagePath,
  modelsPagePath,
  overviewPagePath,
  trainingPagePath,
} from '@/routes/constants';
import { ViewMode } from '@/sections/Workspace/types';
import { useRecoilState } from 'recoil';

import { filterQueryState, viewModeState } from './atoms';
import { WorkspaceSections } from './types';

function useWorkspaceSection() {
  const location = useLocation();

  const match = (path: string) => matchPath(location.pathname, { path, exact: true });

  switch (true) {
    case !!match(modelApisPagePath):
      return WorkspaceSections.ModelAPIs;
    case !!match(modelsPagePath):
      return WorkspaceSections.Models;
    case !!match(chainsPagePath):
      return WorkspaceSections.Chains;
    case !!match(trainingPagePath):
      return WorkspaceSections.Training;
    case !!match(overviewPagePath):
      return WorkspaceSections.Overview;
    default:
      return WorkspaceSections.Overview;
  }
}

function useViewMode(section: WorkspaceSections, minimal: boolean = false) {
  const [viewMode, setViewMode] = useRecoilState(viewModeState(section));

  return { viewMode: minimal ? ViewMode.List : viewMode, setViewMode };
}

function useFilterQuery(section: WorkspaceSections) {
  const [filterQuery, setFilterQuery] = useRecoilState(filterQueryState(section));

  return { filterQuery, setFilterQuery };
}

function useForceUpdateOnWindowResize(debounceMs: number = 100) {
  const [forceUpdateKey, setForceUpdateKey] = useState(0);

  const debouncedResize = useMemo(
    () => debounce(() => setForceUpdateKey((prev) => prev + 1), debounceMs),
    [debounceMs],
  );

  useEffect(() => {
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      debouncedResize.cancel();
    };
  }, [debouncedResize]);

  return forceUpdateKey;
}

function useTableContainerHeight(headerRef: React.RefObject<HTMLDivElement>, key: number): number {
  const [height, setHeight] = useState(0);
  const forceUpdateKey = useForceUpdateOnWindowResize();

  const measureHeight = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      setHeight(element.clientHeight);
    }
  }, []);

  useLayoutEffect(() => {
    if (headerRef.current) {
      measureHeight(headerRef.current);
    }
  }, [measureHeight, headerRef, key, forceUpdateKey]);

  return height;
}

export { useWorkspaceSection, useViewMode, useFilterQuery, useTableContainerHeight };
